import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

import {
  EmptyState,
  ErrorAlert,
  formatDate,
  Loading,
  Meter,
  PageHeader,
  StatusBadge,
} from "../../layout/secure/common";
import SurveyTable from "../../layout/secure/survey-table";
import { listSurveys, messageFromError, surveyOperations } from "../../utils/api";
import useUsage from "./use-usage";

function nearLimit(meter) {
  return meter?.included !== null
    && Number(meter?.included) > 0
    && Number(meter?.used || 0) / Number(meter.included) >= 0.8;
}

function Home() {
  const { data, error, load } = useUsage();
  const [recent, setRecent] = useState(null);
  const [recentError, setRecentError] = useState("");
  const [operations, setOperations] = useState(null);
  const [operationsError, setOperationsError] = useState("");
  useEffect(() => {
    let active = true;
    listSurveys({ page: 1, limit: 5 })
      .then(result => { if (active) setRecent(result.items || []); })
      .catch(requestError => {
        if (active) setRecentError(messageFromError(requestError, "Recent surveys could not be loaded."));
      });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    surveyOperations()
      .then(result => { if (active) setOperations(result); })
      .catch(requestError => {
        if (active) {
          setOperationsError(messageFromError(
            requestError,
            "Operational status could not be loaded."
          ));
        }
      });
    return () => { active = false; };
  }, []);
  if (!data && !error) return <Loading label="Loading your Survey workspace" />;
  const surveys = data?.surveys || {};
  const requests = data?.requests || {};
  const results = data?.results || {};
  const users = data?.users || {};
  const limitWarning = [requests, results].find(nearLimit);

  return (
    <>
      <PageHeader
        title="Survey workspace"
        description="Build surveys, collect answers, and turn results into useful reports."
        action={<Button as={Link} to="/dashboard/surveys/new">New survey</Button>}
      />
      <ErrorAlert error={error} onRetry={load} />
      <ErrorAlert error={recentError} />
      <ErrorAlert error={operationsError} />
      {operations?.alerts?.map(alert => (
        <Alert key={alert.code} variant={alert.severity} role="alert">
          {alert.message}
        </Alert>
      ))}
      {limitWarning ? (
        <Alert variant={Number(limitWarning.remaining) === 0 ? "danger" : "warning"}>
          {Number(limitWarning.remaining) === 0
            ? "A current-period allowance has been exhausted."
            : `${limitWarning.remaining} units remain in a current-period allowance.`}
          {" "}Review Usage before starting a large distribution.
        </Alert>
      ) : null}
      <Row className="g-3 fera-stat-grid">
        <Col sm="6" xl="3"><Card><Card.Body><span>Active surveys</span><strong>{surveys.active || 0}</strong><small>of {surveys.activeLimit ?? "unlimited"} permitted</small></Card.Body></Card></Col>
        <Col sm="6" xl="3"><Card><Card.Body><span>Results received</span><strong>{results.used || 0}</strong><small>this usage period</small></Card.Body></Card></Col>
        <Col sm="6" xl="3"><Card><Card.Body><span>Requests sent</span><strong>{requests.used || 0}</strong><small>this usage period</small></Card.Body></Card></Col>
        <Col sm="6" xl="3"><Card><Card.Body><span>Product users</span><strong>{users.active || 0}</strong><small>of {users.authorized ?? "unlimited"} authorized</small></Card.Body></Card></Col>
      </Row>
      {operations ? (
        <section className="fera-panel mt-4" aria-labelledby="operations-heading">
          <div className="fera-panel-heading">
            <div>
              <p className="fera-eyebrow">Service health</p>
              <h2 id="operations-heading">Survey operations</h2>
            </div>
            <StatusBadge status={
              operations.health === "healthy"
                ? "active"
                : operations.health === "warning"
                  ? "warning"
                  : "failed"
            } />
          </div>
          <div className="fera-operations-grid">
            <div>
              <span>Licensee events waiting</span>
              <strong>{operations.metrics?.licenseUsagePending || 0}</strong>
            </div>
            <div>
              <span>Delivery failures</span>
              <strong>
                {(operations.metrics?.licenseUsageFailed || 0)
                  + (operations.metrics?.webhookFailures || 0)}
              </strong>
            </div>
            <div>
              <span>Export failures</span>
              <strong>{operations.metrics?.exportFailures || 0}</strong>
            </div>
            <div>
              <span>Last reconciliation</span>
              <strong>{formatDate(operations.reconciliation?.checkedAt, "Pending")}</strong>
            </div>
          </div>
          {operations.recentFailures?.length ? (
            <div className="table-responsive mt-3">
              <table className="table fera-table mb-0">
                <caption className="visually-hidden">Recent operational failures</caption>
                <thead>
                  <tr><th>Area</th><th>Status</th><th>Message</th><th>Updated</th></tr>
                </thead>
                <tbody>
                  {operations.recentFailures.slice(0, 5).map(item => (
                    <tr key={`${item.kind}-${item.id}`}>
                      <td>{item.kind}</td>
                      <td><StatusBadge status={item.status} /></td>
                      <td>{item.message}</td>
                      <td>{formatDate(item.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0" role="status">
              No recent delivery or generation failures.
            </p>
          )}
        </section>
      ) : null}
      <Row className="g-4 mt-1">
        <Col lg="8">
          <section className="fera-panel" aria-labelledby="recent-surveys-heading">
            <div className="fera-panel-heading">
              <div><p className="fera-eyebrow">Activity</p><h2 id="recent-surveys-heading">Recent surveys</h2></div>
              <Link to="/dashboard/surveys">View all</Link>
            </div>
            {recent === null && !recentError ? <Loading label="Loading recent surveys" /> : null}
            {recent?.length ? <SurveyTable items={recent} compact /> : null}
            {recent && !recent.length ? (
              <EmptyState
                title="Your surveys will appear here"
                detail="Create a draft, add questions, and publish when you are ready to accept responses."
                action={<Button as={Link} to="/dashboard/surveys/new">Create survey</Button>}
              />
            ) : null}
          </section>
        </Col>
        <Col lg="4">
          <section className="fera-panel fera-usage-card" aria-labelledby="allowance-heading">
            <div className="fera-panel-heading"><div><p className="fera-eyebrow">Current period</p><h2 id="allowance-heading">Plan allowance</h2></div></div>
            <Meter label="Active surveys" value={surveys.active || 0} maximum={surveys.activeLimit} />
            <Meter label="Results" value={results.used || 0} maximum={results.included} />
            <div className="fera-period-row"><span>Plan</span><strong className="text-capitalize">{data?.planCode || "Free"}</strong></div>
            <div className="fera-period-row compact"><span>Period ends</span><strong>{formatDate(data?.usagePeriod?.endsAt)}</strong></div>
            <Link className="btn btn-outline-primary w-100" to="/dashboard/usage">View usage</Link>
          </section>
        </Col>
      </Row>
    </>
  );
}

export default Home;
